import {
  Badge,
  Button,
  Card,
  Column,
  Loader,
  MeatballMenu,
  Row,
  Text,
  useDesignTokens,
} from "@gradience/ui";
import { IconContainer } from "@gradience/ui/dist/components/icon";
import { isDatePast } from "../../../../lib/date-utils";
import { useSelectedTest } from "../../../../domain/use-latest-test";
import { useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { getQueryOptions, useApiDelete } from "../../../../lib/api";
import { useNavigate, useParams } from "@tanstack/react-router";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Student } from "@gradience/api-types";
import {
  GOLD_AWARD_THRESHOLD,
  SILVER_AWARD_THRESHOLD,
} from "../../../../domain/award-thresholds";
import { displayPercentile } from "../../../../lib/percentile";
import { getGroupQueryOptions } from "./utils";

const columnHelper = createColumnHelper<Student>();

export function StudentsTable({ preview }: { preview?: boolean }) {
  const params = useParams({ from: "/$test-slug/groups/$id/" });
  const test = useSelectedTest();
  const students = useQuery(
    getQueryOptions({
      endpoint: "/students",
      queryParams: { groupId: params.id, ...(preview ? { preview: "true" } : {}) },
    })
  );
  const data = useMemo(
    () => (students.data?.data ? [...students.data.data] : []),
    [students.data?.data]
  );

  const designTokens = useDesignTokens();

  const isReviewActive =
    preview ||
    (isDatePast(test.data?.reviewDateStart) &&
      !isDatePast(test.data?.reviewDateEnd));

  const deleteStudent = useApiDelete("/student-lists");
  const onDelete = useCallback(
    async (id: string) => {
      if (window.confirm("Are you sure you want to delete this student?")) {
        await deleteStudent.mutateAsync({
          body: {
            studentIds: [id],
          },
        });
        students.refetch();
      }
    },
    [deleteStudent, students]
  );
  const navigate = useNavigate({ from: "/$test-slug/groups/$id" });

  const studentTableColumns = useMemo(
    () => [
      columnHelper.accessor("firstName", {
        header: "Name",
        cell: ({ row }) => `${row.original.firstName} ${row.original.lastName}`,
      }),
      columnHelper.accessor("grade", {}),
      ...(isReviewActive || students.data?.data.some((s) => s.finalScore)
        ? [
            columnHelper.display({
              id: "correct",
              header: "Score",
              cell: ({ row }) => {
                if (row.original.finalScore) {
                  return (
                    <Row>
                      <Badge>
                        {row.original.finalScore.adjustedScore?.toString() ??
                          "—"}
                      </Badge>
                    </Row>
                  );
                }
                if (isReviewActive && row.original.score) {
                  return (
                    <Row>
                      <Badge>
                        {row.original.score.correctlyFilledIn} /{" "}
                        {row.original.score.numberOfQuestions}
                      </Badge>
                    </Row>
                  );
                }
                return "—";
              },
            }),
          ]
        : []),
      ...(preview || students.data?.data.some((s) => s.finalScore)
        ? [
            columnHelper.accessor("score.percentile", {
              header: "Percentile",
              cell: ({ row }) => {
                const percentile = row.original.finalScore?.percentile;

                if (percentile === undefined || percentile === null) {
                  return "—";
                }

                if (percentile >= GOLD_AWARD_THRESHOLD) {
                  return (
                    <Badge
                      type="gold"
                      style={{ justifySelf: "flex-start" }}
                      icon="medal 2"
                    >
                      {displayPercentile(percentile)}
                    </Badge>
                  );
                }
                if (percentile >= SILVER_AWARD_THRESHOLD) {
                  return (
                    <Badge
                      type="silver"
                      style={{ justifySelf: "flex-start" }}
                      icon="medal 2"
                    >
                      {displayPercentile(percentile)}
                    </Badge>
                  );
                }

                return displayPercentile(percentile);
              },
            }),
          ]
        : []),
      ...(isDatePast(test.data?.configurationDateEnd)
        ? []
        : [
            columnHelper.display({
              id: "actions",
              header: "Actions",
              cell: ({ row }) => (
                <MeatballMenu
                  options={[
                    { label: "Delete", value: "delete" },
                    { label: "Edit", value: "edit" },
                  ]}
                  onSelect={(option) => {
                    switch (option) {
                      case "delete":
                        onDelete(row.original.id);
                        break;
                      case "edit":
                        navigate({
                          search: (prev) => ({
                            ...prev,
                            groupModal: "edit-student",
                            editingStudentId: row.original.id,
                          }),
                        });
                        break;
                    }
                  }}
                />
              ),
            }),
          ]),
    ],
    [navigate, onDelete, test.data?.configurationDateEnd, isReviewActive, students.data]
  );

  const table = useReactTable({
    data,
    columns: studentTableColumns,
    getCoreRowModel: getCoreRowModel(),
  });

  const group = useQuery(
    getGroupQueryOptions({ testId: test.data?.id, groupId: params.id })
  );

  return (
    <Card
      style={{
        flex: 1,
        padding: 24,
        gap: 24,
      }}
    >
      <Row
        gap={16}
        style={{
          alignItems: "center",
        }}
      >
        <IconContainer icon="Students" />
        <Column style={{ flex: 1 }}>
          <Text textStyle="20px - Bold">Students</Text>
          <Text textStyle="14px - Medium">
            Add students to this group by uploading a spreadsheet or entering
            them manually.
          </Text>
        </Column>
        {isDatePast(test.data?.configurationDateEnd) ? null : (
          <Button
            icon="plus-01"
            disabled={students.isLoading}
            text="Add Students"
            variant="subdued"
            size="small"
            onPress={() => {
              navigate({
                search: (prev) => ({
                  ...prev,
                  groupModal: "add-students",
                }),
              });
            }}
          />
        )}
      </Row>
      {group.data?.numberOfStudents ? (
        students.isLoading ? (
          <Loader />
        ) : (
          <table
            style={{
              borderCollapse: "collapse",
            }}
          >
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      style={{
                        padding: 16,
                      }}
                    >
                      {header.isPlaceholder ? null : (
                        <Row gap={6}>
                          <Text
                            textStyle="subHeading"
                            style={{
                              textTransform: "uppercase",
                              textAlign: "start",
                            }}
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                          </Text>
                        </Row>
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      style={{
                        padding: "24px 16px",
                        borderTop: `1px solid ${designTokens.colors.border.subdued}`,
                      }}
                    >
                      <Text textStyle="14px - Medium">
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </Text>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
            <tfoot>
              {table.getFooterGroups().map((footerGroup) => (
                <tr key={footerGroup.id}>
                  {footerGroup.headers.map((header) => (
                    <th key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.footer,
                            header.getContext()
                          )}
                    </th>
                  ))}
                </tr>
              ))}
            </tfoot>
          </table>
        )
      ) : (
        <Row
          style={{
            padding: 16,
            backgroundColor: designTokens.colors.surface.Subdued,
            borderRadius: 12,
            justifyContent: "center",
          }}
        >
          <Text textStyle="14px - Semibold">No students added yet.</Text>
        </Row>
      )}
    </Card>
  );
}
